// @ts-check

/**
 * @file Renders task details in a modal.
 */

import _ from "gettext-universal/build/src/translate.js"
import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {Link} from "expo-router"
import {memo} from "react"
import PropTypes from "prop-types"
import PropTypesExact from "prop-types-exact"
import {Linking, Pressable, Text, View} from "react-native"
import {ScrollView} from "react-native-propforge"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import useNow from "set-state-compare/build/use-now.js"
import Button from "../button"
import DetailValue from "../detail-value"
import TaskBoardPlacements from "./board-placements"
import TaskModalComments from "./modal-comments"
import TaskModalTimelogs from "./modal-timelogs"
import TaskModalUsers from "./modal-users"
import TimelogsEditScreen from "../../screens/timelogs/edit-screen"
import Modal from "../modal"
import SharedRenderHtml from "../render-html"
import useCurrentUser from "../../auth/use-current-user"
import RemoteTask from "../../frontend-models/task"
import {canMarkTaskAgentReady, canRespondToAutomationPlan, translatedAgentState, translatedAutomationRuntimeStatus} from "../../shared/agent-state"
import {translatedTaskState} from "../../shared/task-state"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @typedef {RemoteTask | InstanceType<typeof import("../../models/task.js").default>} TaskReadRecord */
/** @typedef {InstanceType<typeof import("../../models/project.js").default>} LocalProject */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {(...args: unknown[]) => unknown} TaskDetailModalCallback */
/** @typedef {() => Promise<void>} TaskDetailModalCommentCreatedCallback */

/**
 * @typedef {object} TaskDetailModalProps
 * @property {boolean} [approvingAutomationPlan] Whether an approval comment is being created.
 * @property {string} [cancelAutomationRunHref] Comment route for cancelling the automation run.
 * @property {string} [currentBoardId] Board containing the modal's selected card.
 * @property {TaskDetailModalCallback} onApproveAutomationPlanPress Approve-plan callback.
 * @property {TaskDetailModalCallback} onClosePress Close callback.
 * @property {TaskDetailModalCommentCreatedCallback} [onCommentCreated] Callback after a comment is created.
 * @property {(response: import("../../../../shared/task-board-placement-command-types.js").TaskBoardRemoveFromBoardResponse) => void} [onCurrentBoardPlacementRemoved] Callback after the selected card's placement is removed.
 * @property {TaskDetailModalCallback} onDeletePress Delete callback.
 * @property {TaskDetailModalCallback} [onDismiss] Callback fired after the modal finishes fading out.
 * @property {TaskDetailModalCallback} onEditPress Edit callback.
 * @property {TaskDetailModalCallback} onMarkAgentReadyPress Mark-agent-ready callback.
 * @property {boolean} [online] Whether online-only commands are available.
 * @property {import("../../database/project-replica.js").OpenProjectReplica | null} [replica] Open project replica for offline timelog entry.
 * @property {LocalProject | null} [project] Local project catalog record.
 * @property {string} [projectId] Owning project replica id.
 * @property {string} [requestAutomationChangesHref] Comment route for requesting plan changes.
 * @property {TaskReadRecord | null} [task] Task to display.
 * @property {Record<string, string | string[] | undefined>} [params] Current route params for building links.
 * @property {string} [testID] Base test id for rendered elements.
 * @property {boolean} visible Whether the modal is visible.
 */

/** @typedef {{activeTab: "general" | "timelogs" | "users", editingTimelog: import("../../frontend-models/timelog").default | null, timelogEditorVisible: boolean, timelogListKey: number, usersCount: number | null}} TaskDetailModalState */

/**
 * Modal showing task details.
 * @augments {ShapeComponent<TaskDetailModalProps, TaskDetailModalState>}
 */
class TaskDetailModal extends ShapeComponent {
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  /** @type {ReturnType<typeof useCurrentUser>} */
  currentUser = this.hookField()

  /** @type {TaskDetailModalState} */
  state = {activeTab: "general", editingTimelog: null, timelogEditorVisible: false, timelogListKey: 0, usersCount: null}

  static propTypes = PropTypesExact({
    approvingAutomationPlan: PropTypes.bool,
    cancelAutomationRunHref: PropTypes.string,
    currentBoardId: PropTypes.string,
    onApproveAutomationPlanPress: PropTypes.func.isRequired,
    onClosePress: PropTypes.func.isRequired,
    onCommentCreated: PropTypes.func,
    onCurrentBoardPlacementRemoved: PropTypes.func,
    onDeletePress: PropTypes.func.isRequired,
    onDismiss: PropTypes.func,
    onEditPress: PropTypes.func.isRequired,
    onMarkAgentReadyPress: PropTypes.func.isRequired,
    online: PropTypes.bool,
    params: PropTypes.object,
    replica: PropTypes.object,
    project: PropTypes.object,
    projectId: PropTypes.string,
    requestAutomationChangesHref: PropTypes.string,
    task: PropTypes.object,
    testID: PropTypes.string,
    visible: PropTypes.bool.isRequired
  })
  static defaultProps = {
    approvingAutomationPlan: false,
    cancelAutomationRunHref: undefined,
    currentBoardId: undefined,
    onCommentCreated: undefined,
    onCurrentBoardPlacementRemoved: undefined,
    onDismiss: undefined,
    online: true,
    params: undefined,
    project: null,
    projectId: undefined,
    replica: null,
    requestAutomationChangesHref: undefined,
    task: null,
    testID: "taskDetailModal"
  }

  /** Initializes locale handling and resets per-task tab state when the selected task changes. */
  setup() {
    useLocale()
    this.colors = useThemeColors()
    this.currentUser = useCurrentUser()
    this.themeName = useThemeName()
    useNow(() => {
      this.s.activeTab = "general"
      this.s.editingTimelog = null
      this.s.timelogEditorVisible = false
      this.s.timelogListKey = 0
      this.s.usersCount = null
    }, [this.p.visible, this.p.task?.id()])
  }

  /**
   * Renders task details when visible.
   * @returns {ReactNode} Task detail modal content.
   */
  render() {
    /** @type {TaskReadRecord | null} */
    const task = this.p.task
    const colors = this.tt.colors
    const testID = this.p.testID || "taskDetailModal"
    const themeName = this.tt.themeName
    const approvalButtonDisabled = !this.p.online || this.p.approvingAutomationPlan
    if (!task) {
      return (
        <Modal
          cardStyle={styles.card ||= {
            borderRadius: 20,
            maxHeight: "90%",
            maxWidth: 560,
            overflow: "hidden",
            padding: 24
          }}
          isVisible={false}
          onDismiss={this.p.onDismiss}
          onRequestClose={this.p.onClosePress}
          testID={testID}
        />
      )
    }
    const remoteTask = task instanceof RemoteTask ? task : null
    const project = this.p.project || remoteTask?.project() || null
    const projectId = this.p.projectId || task.projectId()
    const automationAssignable = project?.automationAssignable() ?? remoteTask?.project()?.automationAssignable() ?? false
    const description = task.description()
    const taskId = task.id()
    const agentClaimedBy = task.agentClaimedBy() || null
    const runtimeLinks = automationRuntimeLinksForTask(task)
    const runtimeStatus = translatedAutomationRuntimeStatus(task.automationRuntimeStatus() || null)
    const hasRuntimeLinks = Object.keys(runtimeLinks).length > 0
    const canRespondToPlan = canRespondToAutomationPlan({
      agentState: task.agentState(),
      runtimeStatus: task.automationRuntimeStatus()
    })
    const canMarkReady = canMarkTaskAgentReady({
      agentState: task.agentState(),
      taskState: task.state(),
      automationAssignable
    })
    const localCanMutate = Boolean(this.tt.currentUser.user && (
      this.tt.currentUser.user.admin ||
      String(this.tt.currentUser.user.id) === String(task.userId())
    ))
    const canCreateTimelog = Boolean(this.p.online && (!remoteTask || remoteTask.can("createTimelog"))) ||
      (Boolean(this.p.replica) && !this.p.online && localCanMutate)
    const canDestroy = Boolean(this.p.online && (remoteTask ? remoteTask.can("destroy") : localCanMutate))
    const canUpdate = Boolean(this.p.online && (remoteTask ? remoteTask.can("update") : localCanMutate))
    return (
      <Modal
        cardStyle={styles.card ||= {
          borderRadius: 20,
          maxHeight: "90%",
          maxWidth: 560,
          overflow: "hidden",
          padding: 24
        }}
        isVisible={this.p.visible}
        onDismiss={this.p.onDismiss}
        onRequestClose={this.p.onClosePress}
        testID={testID}
      >
          <View
            style={styles.header ||= {
              alignItems: "flex-start",
              flexDirection: "row",
              justifyContent: "space-between",
              marginBottom: 20
            }}
            testID={`${testID}/header`}
          >
            <View
              style={styles.headerContent ||= {
                flex: 1,
                paddingRight: 16
              }}
              testID={`${testID}/headerContent`}
            >
              <Text
                style={styles[`eyebrow-${themeName}`] ||= {
                  color: colors.textMuted,
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 8
                }}
                testID={`${testID}/eyebrow`}
              >
                {_("Task details")}
              </Text>
              <Text
                style={styles[`title-${themeName}`] ||= {
                  color: colors.text,
                  fontSize: 24,
                  fontWeight: 700,
                  lineHeight: 30
                }}
                testID={`${testID}/title`}
              >
                {task.name() || _("Untitled task")}
              </Text>
            </View>
            <Pressable
              onPress={this.p.onClosePress}
              style={styles[`closeButton-${themeName}`] ||= {
                alignItems: "center",
                backgroundColor: colors.primary,
                borderRadius: 999,
                flexDirection: "row",
                justifyContent: "center",
                minHeight: 40,
                minWidth: 88,
                paddingHorizontal: 16,
                paddingVertical: 10
              }}
              testID={`${testID}/closeButton`}
            >
              <View
                style={styles.closeButtonIcon ||= {
                  marginRight: 8,
                  width: 14
                }}
                testID={`${testID}/closeButton/icon`}
              >
                <FontAwesome6 color={colors.onPrimary} name="xmark" size={14} />
              </View>
              <Text
                style={styles[`closeButtonText-${themeName}`] ||= {
                  color: colors.onPrimary,
                  fontSize: 14,
                  fontWeight: 600
                }}
                testID={`${testID}/closeButton/text`}
              >
                {_("Close")}
              </Text>
            </Pressable>
          </View>
          <View style={styles.tabs ||= {flexDirection: "row", flexWrap: "wrap", gap: 8, marginBottom: 16}} testID={`${testID}/tabs`}>
            <Button
              label={_("General")}
              onPress={this.tt.onGeneralTabPress}
              testID={`${testID}/tabs/general`}
              variant={this.s.activeTab === "general" ? "primary" : "outline"}
            />
            <Button
              label={_("Time logs")}
              onPress={this.tt.onTimelogsTabPress}
              testID={`${testID}/tabs/timelogs`}
              variant={this.s.activeTab === "timelogs" ? "primary" : "outline"}
            />
            <Button
              label={this.s.usersCount != null ? `${_("Users")} (${this.s.usersCount})` : _("Users")}
              onPress={this.tt.onUsersTabPress}
              testID={`${testID}/tabs/users`}
              variant={this.s.activeTab === "users" ? "primary" : "outline"}
            />
          </View>
          {this.s.activeTab === "general" && <>
          <ScrollView
            contentContainerStyle={styles.detailsScrollContent ||= {
              paddingBottom: 4
            }}
            style={styles.detailsScroll ||= {
              flexShrink: 1
            }}
            testID={`${testID}/detailsScroll`}
          >
          <View
            style={styles[`details-${themeName}`] ||= {
              borderTopColor: colors.divider,
              borderTopWidth: 1,
              paddingTop: 16
            }}
            testID={`${testID}/details`}
          >
            {detailRow({
              colors,
              href: projectId ? `/tasks/${taskId}?projectId=${projectId}` : `/tasks/${taskId}`,
              label: _("Task ID"),
              modalTestID: testID,
              testID: "taskId",
              themeName,
              value: String(taskId)
            })}
            {detailRow({
              colors,
              href: project ? `/projects/${project.id()}` : undefined,
              label: _("Project"),
              modalTestID: testID,
              testID: "project",
              themeName,
              value: project?.name() || _("Project")
            })}
            <TaskBoardPlacements
              currentBoardId={this.p.currentBoardId}
              key={taskId}
              onCurrentBoardPlacementRemoved={this.p.onCurrentBoardPlacementRemoved}
              online={this.p.online}
              params={this.p.params}
              projectId={String(projectId)}
              task={task}
              testID={`${testID}/boardPlacements`}
            />
            {detailRow({
              colors,
              label: _("State"),
              modalTestID: testID,
              testID: "state",
              themeName,
              value: translatedTaskState(task.state())
            })}
            {detailRow({
              colors,
              label: _("Agent state"),
              modalTestID: testID,
              testID: "agentState",
              themeName,
              value: translatedAgentState(task.agentState())
            })}
            {runtimeStatus &&
              detailRow({
                colors,
                label: _("Automation status"),
                modalTestID: testID,
                testID: "automationRuntimeStatus",
                themeName,
                value: runtimeStatus
              })
            }
            {agentClaimedBy &&
              detailRow({
                colors,
                label: _("Claimed by"),
                modalTestID: testID,
                testID: "agentClaimedBy",
                themeName,
                value: agentClaimedBy
              })
            }
            {hasRuntimeLinks &&
              <View
                style={styles[`detailRow-${themeName}`] ||= {
                  borderBottomColor: colors.divider,
                  borderBottomWidth: 1,
                  paddingVertical: 12
                }}
                testID={`${testID}/automationRuntimeLinks/row`}
              >
                <Text
                  style={styles[`detailLabel-${themeName}`] ||= {
                    color: colors.textMuted,
                    fontSize: 13,
                    fontWeight: 600,
                    marginBottom: 4
                  }}
                  testID={`${testID}/automationRuntimeLinks/label`}
                >
                  {_("Automation links")}
                </Text>
                <View
                  style={styles.runtimeLinks ||= {
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 8,
                    paddingTop: 4
                  }}
                  testID={`${testID}/automationRuntimeLinks/value`}
                >
                  {runtimeLinks.agentRunUrl &&
                    runtimeLinkButton({
                      colors,
                      iconName: "robot",
                      label: _("Run"),
                      onPress: this.tt.onAgentRunLinkPress,
                      testID: `${testID}/automationRuntimeLinks/run`,
                      themeName
                    })
                  }
                  {runtimeLinks.logsUrl &&
                    runtimeLinkButton({
                      colors,
                      iconName: "list",
                      label: _("Logs"),
                      onPress: this.tt.onLogsLinkPress,
                      testID: `${testID}/automationRuntimeLinks/logs`,
                      themeName
                    })
                  }
                  {runtimeLinks.projectEnvironmentInstanceUrl &&
                    runtimeLinkButton({
                      colors,
                      iconName: "desktop",
                      label: _("Instance"),
                      onPress: this.tt.onInstanceLinkPress,
                      testID: `${testID}/automationRuntimeLinks/instance`,
                      themeName
                    })
                  }
                  {runtimeLinks.statusUrl &&
                    runtimeLinkButton({
                      colors,
                      iconName: "circle-info",
                      label: _("Status"),
                      onPress: this.tt.onStatusLinkPress,
                      testID: `${testID}/automationRuntimeLinks/status`,
                      themeName
                    })
                  }
                </View>
              </View>
            }
            <View
              style={styles[`detailRow-${themeName}`] ||= {
                borderBottomColor: colors.divider,
                borderBottomWidth: 1,
                paddingVertical: 12
              }}
              testID={`${testID}/description/row`}
            >
              <Text
                style={styles[`detailLabel-${themeName}`] ||= {
                  color: colors.textMuted,
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 4
                }}
                testID={`${testID}/description/label`}
              >
                {_("Description")}
              </Text>
              <View
                style={styles.descriptionValue ||= {
                  paddingVertical: 4
                }}
                testID={`${testID}/description/value`}
              >
                {description &&
                  <SharedRenderHtml
                    source={description}
                    testID={`${testID}/description/html`}
                  />
                }
                {!description &&
                  <Text
                    style={styles[`descriptionFallback-${themeName}`] ||= {
                      color: colors.text,
                      fontSize: 16,
                      fontWeight: 600
                    }}
                    testID={`${testID}/description/fallback`}
                  >
                    -
                  </Text>
                }
              </View>
            </View>
            <TaskModalComments
              online={this.p.online}
              projectId={String(projectId)}
              task={task}
              testID={testID}
            />
          </View>
          </ScrollView>
          <View
            style={styles.actions ||= {
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 10,
              justifyContent: "flex-end",
              marginTop: 20
            }}
            testID={`${testID}/actions`}
          >
            {canRespondToPlan &&
              <Button
                disabled={approvalButtonDisabled}
                iconName="check"
                label={approvalButtonDisabled ? _("Approving...") : _("Approve plan")}
                onPress={this.p.onApproveAutomationPlanPress}
                testID={`${testID}/approveAutomationPlanButton`}
              />
            }
            {canRespondToPlan && this.p.requestAutomationChangesHref &&
              <Button
                disabled={!this.p.online}
                href={this.p.requestAutomationChangesHref}
                iconName="message"
                label={_("Request changes")}
                testID={`${testID}/requestAutomationChangesButton`}
                variant="outline"
              />
            }
            {canRespondToPlan && this.p.cancelAutomationRunHref &&
              <Button
                disabled={!this.p.online}
                href={this.p.cancelAutomationRunHref}
                iconName="ban"
                label={_("Cancel run")}
                testID={`${testID}/cancelAutomationRunButton`}
                variant="destructive-outline"
              />
            }
            {canMarkReady &&
              <Pressable
                disabled={!this.p.online}
                onPress={this.p.onMarkAgentReadyPress}
                style={styles[`markAgentReadyButton-${themeName}`] ||= {
                  alignItems: "center",
                  backgroundColor: colors.primary,
                  borderRadius: 10,
                  flexDirection: "row",
                  paddingHorizontal: 14,
                  paddingVertical: 10
                }}
                testID={`${testID}/markAgentReadyButton`}
              >
                <View
                  style={styles.markAgentReadyButtonIcon ||= {
                    marginRight: 8,
                    width: 14
                  }}
                  testID={`${testID}/markAgentReadyButton/icon`}
                >
                  <FontAwesome6 color={colors.onPrimary} name="robot" size={14} />
                </View>
                <Text
                  style={styles[`markAgentReadyButtonText-${themeName}`] ||= {
                    color: colors.onPrimary,
                    fontSize: 14,
                    fontWeight: 600
                  }}
                  testID={`${testID}/markAgentReadyButton/text`}
                >
                  {_("Assign to AI")}
                </Text>
              </Pressable>
            }
            {canUpdate &&
              <Pressable
                onPress={this.p.onEditPress}
                style={styles[`editButton-${themeName}`] ||= {
                  alignItems: "center",
                  backgroundColor: colors.infoBackground,
                  borderRadius: 10,
                  flexDirection: "row",
                  paddingHorizontal: 14,
                  paddingVertical: 10
                }}
                testID={`${testID}/editButton`}
              >
                <View
                  style={styles.editButtonIcon ||= {
                    marginRight: 8,
                    width: 14
                  }}
                  testID={`${testID}/editButton/icon`}
                >
                  <FontAwesome6 color={colors.link} name="pen-to-square" size={14} />
                </View>
                <Text
                  style={styles[`editButtonText-${themeName}`] ||= {
                    color: colors.link,
                    fontSize: 14,
                    fontWeight: 600
                  }}
                  testID={`${testID}/editButton/text`}
                >
                  {_("Edit task")}
                </Text>
              </Pressable>
            }
            {canDestroy &&
              <Pressable
                onPress={this.p.onDeletePress}
                style={styles[`deleteButton-${themeName}`] ||= {
                  alignItems: "center",
                  backgroundColor: colors.dangerBackground,
                  borderRadius: 10,
                  flexDirection: "row",
                  paddingHorizontal: 14,
                  paddingVertical: 10
                }}
                testID={`${testID}/deleteButton`}
              >
                <View
                  style={styles.deleteButtonIcon ||= {
                    marginRight: 8,
                    width: 14
                  }}
                  testID={`${testID}/deleteButton/icon`}
                >
                  <FontAwesome6 color={colors.dangerText} name="trash" size={14} />
                </View>
                <Text
                  style={styles[`deleteButtonText-${themeName}`] ||= {
                    color: colors.dangerText,
                    fontSize: 14,
                    fontWeight: 600
                  }}
                  testID={`${testID}/deleteButton/text`}
                >
                  {_("Delete Task")}
                </Text>
              </Pressable>
            }
          </View>
          </>}
          {this.s.activeTab === "timelogs" &&
            <TaskModalTimelogs
              key={this.s.timelogListKey}
              online={this.p.online}
              onEditTimelog={this.tt.onEditTimelog}
              projectId={String(projectId)}
              replica={this.p.replica}
              task={task}
              testID={testID}
            />
          }
          {this.s.activeTab === "users" &&
            <TaskModalUsers
              key={taskId}
              onCountChange={this.tt.onUsersCountChange}
              online={this.p.online}
              projectId={String(projectId)}
              task={task}
              testID={testID}
            />
          }
          <Modal
            cardStyle={styles.timelogEditorCard ||= {borderRadius: 20, maxHeight: "90%", maxWidth: 560, overflow: "hidden", padding: 24}}
            isVisible={this.s.timelogEditorVisible && (Boolean(this.s.editingTimelog) || canCreateTimelog)}
            onRequestClose={this.tt.onCloseTimelogEditor}
            testID={`${testID}/timelogEditor`}
          >
            <ScrollView
              style={styles.timelogEditorScroll ||= {flexShrink: 1}}
              testID={`${testID}/timelogEditor/scroll`}
            >
              <TimelogsEditScreen
                key={this.s.editingTimelog ? this.s.editingTimelog.id() : `new-${taskId}`}
                online={this.p.online}
                onSaved={this.tt.onTimelogSaved}
                prefilledTaskID={taskId}
                projectId={String(projectId)}
                replica={this.p.replica}
                timelog={this.s.editingTimelog}
              />
            </ScrollView>
          </Modal>
      </Modal>
    )
  }

  /** Shows the general task details tab. */
  onGeneralTabPress = () => { this.s.activeTab = "general" }

  /** Shows the task-scoped time logs tab. */
  onTimelogsTabPress = () => { this.s.activeTab = "timelogs" }

  /** Shows the task-scoped assigned users tab. */
  onUsersTabPress = () => { this.s.activeTab = "users" }

  /**
   * Stores the assigned-user count reported by the users tab for the tab badge.
   * @param {number} count Loaded assigned-user count.
   */
  onUsersCountChange = (count) => { this.s.usersCount = count }

  /**
   * Reports whether the signed-in user may mutate this task's timelogs offline.
   * @param {{userId: () => string | number}} task Local replica task.
   * @returns {boolean} Whether the signed-in user owns the task (or is admin), mirroring
   * the server-side timelog create ability for offline entry.
   */
  localCanMutateTask = (task) => {
    const user = this.tt.currentUser.user
    return Boolean(user && (user.admin || String(user.id) === String(task.userId())))
  }

  /**
   * Opens the timelog editor for an existing row, or a new one when null is supplied.
   * @param {import("../../frontend-models/timelog").default | null} timelog Existing timelog, or null for a new entry.
   * @throws {Error} When the task is missing or the mutation is not permitted.
   */
  onEditTimelog = (timelog) => {
    const task = this.p.task
    if (!task) throw new Error("Expected a task before opening its timelog editor")
    if (!this.p.online && !this.p.replica) throw new Error("Timelog commands require an internet connection")
    const remoteTask = task instanceof RemoteTask ? task : null
    if (this.p.online && !timelog && remoteTask && !remoteTask.can("createTimelog")) throw new Error("Not permitted to create a timelog for this task")
    if (!this.p.online && !timelog && !this.tt.localCanMutateTask(task)) throw new Error("Not permitted to create a timelog for this task")

    this.s.editingTimelog = timelog
    this.s.timelogEditorVisible = true
  }

  /** Closes the nested time log editor. */
  onCloseTimelogEditor = () => { this.s.timelogEditorVisible = false }

  /**
   * Closes the editor and refreshes the time-log tab after saving.
   * @param {import("../../frontend-models/timelog").default} _timelog Timelog that was persisted.
   */
  onTimelogSaved = async (_timelog) => {
    this.s.timelogEditorVisible = false
    this.s.timelogListKey += 1
  }

  /** Opens the external automation run link. */
  onAgentRunLinkPress = () => {
    this.openRuntimeLink("agentRunUrl")
  }

  /** Opens the automation run logs link. */
  onLogsLinkPress = () => {
    this.openRuntimeLink("logsUrl")
  }

  /** Opens the runtime instance link. */
  onInstanceLinkPress = () => {
    this.openRuntimeLink("projectEnvironmentInstanceUrl")
  }

  /** Opens the runtime status link. */
  onStatusLinkPress = () => {
    this.openRuntimeLink("statusUrl")
  }

  /**
   * Opens one stored runtime link for the current task.
   * @param {"agentRunUrl" | "logsUrl" | "projectEnvironmentInstanceUrl" | "statusUrl"} key Stored link slot to open.
   * @returns {void} The browser handles the external URL.
   */
  openRuntimeLink(key) {
    const task = this.p.task
    if (!task) return

    const url = automationRuntimeLinksForTask(task)[key]

    if (url) {
      void Linking.openURL(url)
    }
  }
}

/** @type {NamedExoticComponent<TaskDetailModalProps>} */
const TaskDetailModalComponent = memo(shapeComponent(TaskDetailModal))

export default TaskDetailModalComponent

/**
 * Renders one task detail row.
 * @param {{colors: import("../../shared/theme").BoardThemeColors, href?: string, label: string, modalTestID: string, testID: string, themeName: string, value: string}} args Detail row values.
 * @returns {ReactNode} Rendered detail row.
 */
function detailRow({colors, href, label, modalTestID, testID, themeName, value}) {
  const valueStyle = href
    ? styles[`detailValueLink-${themeName}`] ||= {
      color: colors.link,
      fontSize: 16,
      fontWeight: 600,
      textDecorationLine: "underline"
    }
    : styles[`detailValue-${themeName}`] ||= {
      color: colors.text,
      fontSize: 16,
      fontWeight: 600
    }

  return (
    <View
      collapsable={false}
      style={styles[`detailRow-${themeName}`] ||= {
        borderBottomColor: colors.divider,
        borderBottomWidth: 1,
        paddingVertical: 12
      }}
      testID={`${modalTestID}/${testID}/row`}
    >
      <Text
        style={styles[`detailLabel-${themeName}`] ||= {
          color: colors.textMuted,
          fontSize: 13,
          fontWeight: 600,
          marginBottom: 4
        }}
        testID={`${modalTestID}/${testID}/label`}
      >
        {label}
      </Text>
      {href
        ? <Link href={href} testID={`${modalTestID}/${testID}/link`}>
            <Text
              accessible
              accessibilityLabel={value}
              style={valueStyle}
              testID={`${modalTestID}/${testID}/value`}
            >
              {value}
            </Text>
          </Link>
        : <DetailValue
            style={valueStyle}
            testID={`${modalTestID}/${testID}/value`}
            value={value}
          />
      }
    </View>
  )
}

/**
 * Renders one compact runtime link button.
 * @param {{colors: import("../../shared/theme").BoardThemeColors, iconName: string, label: string, onPress: () => void, testID: string, themeName: string}} args Button inputs.
 * @returns {ReactNode} Rendered button.
 */
function runtimeLinkButton({colors, iconName, label, onPress, testID, themeName}) {
  return (
    <Pressable
      onPress={onPress}
      style={styles[`runtimeLinkButton-${themeName}`] ||= {
        alignItems: "center",
        backgroundColor: colors.infoBackground,
        borderRadius: 10,
        flexDirection: "row",
        paddingHorizontal: 10,
        paddingVertical: 8
      }}
      testID={testID}
    >
      <View
        style={styles.runtimeLinkButtonIcon ||= {
          marginRight: 6,
          width: 14
        }}
        testID={`${testID}/icon`}
      >
        <FontAwesome6 color={colors.link} name={iconName} size={13} />
      </View>
      <Text
        style={styles[`runtimeLinkButtonText-${themeName}`] ||= {
          color: colors.link,
          fontSize: 13,
          fontWeight: 600
        }}
        testID={`${testID}/text`}
      >
        {label}
      </Text>
    </Pressable>
  )
}

/**
 * Reads public runtime links from a task.
 * @param {TaskReadRecord} task Task to inspect.
 * @returns {Record<string, string>} Safe link URLs keyed by public purpose.
 */
function automationRuntimeLinksForTask(task) {
  const links = task.automationRuntimeLinks()

  if (!links || typeof links !== "object" || Array.isArray(links)) return {}

  /** @type {Record<string, string>} */
  const result = {}

  for (const key of ["agentRunUrl", "logsUrl", "projectEnvironmentInstanceUrl", "statusUrl"]) {
    const value = links[key]

    if (typeof value === "string" && value.trim()) {
      result[key] = value.trim()
    }
  }

  return result
}
